import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS = {
  new: { label: "جديد", cls: "bg-gold/15 text-gold-dark" },
  contacted: { label: "تم التواصل", cls: "bg-forest-900/10 text-forest-900" },
  closed: { label: "مغلق", cls: "bg-brick/10 text-brick" },
};

export default function InquiriesTab() {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/inquiries", { params: { search } });
        setItems(data);
        setError("");
      } catch (e) {
        setError(apiError(e));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) {
      alert(apiError(e));
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("ar", { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-inquiries-tab">
      <div className="relative">
        <Search className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-moss" />
        <input
          className="w-full rounded-xl border border-line bg-white py-3 pl-4 pr-11 text-sm focus:border-gold focus:outline-none"
          placeholder="ابحث بالاسم أو رقم الهاتف أو نص الرسالة…"
          value={search}
          data-testid="admin-inquiries-search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="mt-4 rounded-xl bg-brick/10 px-4 py-3 text-sm text-brick" data-testid="admin-inquiries-error">{error}</p>}

      {!items && (
        <div className="mt-10 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-gold" data-testid="admin-inquiries-loading" />
        </div>
      )}

      {items && items.length === 0 && (
        <p className="mt-8 rounded-xl border border-dashed border-line p-10 text-center text-sm text-moss" data-testid="admin-inquiries-empty">
          لا توجد استفسارات بعد
        </p>
      )}

      <div className="mt-5 space-y-3">
        {(items || []).map((it, i) => (
          <article key={it.id} className="rounded-2xl border border-line bg-white p-5" data-testid={`inquiry-card-${i}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-head text-sm font-extrabold text-forest-950">
                  {it.name}
                  {it.salon_name && <span className="font-normal text-moss"> — {it.salon_name}</span>}
                </p>
                <p className="num-ltr mt-1 text-sm text-moss">{it.phone}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-moss">{fmt(it.created_at)}</span>
                <select
                  value={it.status}
                  data-testid={`inquiry-status-${i}`}
                  onChange={(e) => setStatus(it.id, e.target.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold outline-none ${STATUS[it.status]?.cls || ""}`}
                >
                  {Object.entries(STATUS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-3 rounded-xl bg-cream p-3 text-sm leading-relaxed text-ink" data-testid={`inquiry-message-${i}`}>
              {it.message}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
